import type { HTMLProps, ReactNode } from 'react';
import { forwardRef } from 'react';

import { Color } from '@tmh-website/shared/colors';
import { ButtonThemeColor, buttonThemes } from '@tmh-website/ui/Button';
import LoadingSpinner from '@tmh-website/ui/LoadingSpinner';

import * as Styled from './ButtonOutlined.styles';

export interface ButtonOutlinedProps
  extends Omit<HTMLProps<HTMLButtonElement | HTMLAnchorElement>, 'type' | 'ref' | 'as' | 'forwardedAs'> {
  children: ReactNode;
  themeColor?: ButtonThemeColor;
  isLoading?: boolean;
  fullWidth?: boolean;
  ref?: any;
  type?: string;
  /**
   * Optional custom Icon on the left
   */
  iconLeft?: any;
  /**
   * Optional custom Icon on the right
   */
  iconRight?: any;
  /**
   * Optional custom gap between text and icon
   */
  gap?: string;
  normalColorOnHover?: boolean;
  useRegularTextColor?: boolean;
  isSmall?: boolean;
}

/**
 * The outline version of the regular TMH button.
 * This button has a "normal" hover effect.
 */
const ButtonOutlined = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonOutlinedProps>(
  (
    {
      themeColor = Color.Blue_500_Ultramarine,
      isLoading = false,
      children,
      type,
      iconLeft,
      iconRight,
      gap = '8px',
      fullWidth,
      normalColorOnHover,
      useRegularTextColor,
      isSmall = false,
      ...props
    },
    ref
  ) => {
    return (
      // @ts-ignore - FIXME: There are some weird type errors.
      <Styled.Container
        ref={ref}
        as={props.href ? 'a' : 'button'}
        type={type}
        {...props}
        $themeColor={themeColor}
        $gap={gap}
        $isLoading={isLoading}
        $isSmall={isSmall}
        $fullWidth={fullWidth}
        $normalColorOnHover={normalColorOnHover}
        $useRegularTextColor={useRegularTextColor}>
        {iconLeft && <Styled.IconContainerLeft>{iconLeft}</Styled.IconContainerLeft>}
        <span>{children}</span>
        {iconRight && <Styled.IconContainerRight>{iconRight}</Styled.IconContainerRight>}
        {isLoading && <LoadingSpinner color={buttonThemes[themeColor].text} absolutePosition noMargin />}
      </Styled.Container>
    );
  }
);

ButtonOutlined.displayName = 'ButtonOutlined';

export default ButtonOutlined;
